import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Upload as UploadIcon,
  ListAlt as ListIcon,
  Analytics as AnalyticsIcon,
  Assignment as ReturnsListIcon,
  AssignmentReturn as ReturnReasonIcon,
} from "@mui/icons-material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { useNavigate } from "react-router-dom";
import {
  ORDER_MANAGEMENT_IMPORT,
  ORDER_MANAGEMENT_LIST,
  ORDER_MANAGEMENT_STATISTICS,
  ORDER_MANAGEMENT_RETURNS,
  ORDER_MANAGEMENT_PAYMENT_PROCESS,
  ORDER_MANAGEMENT_MARKETING_EXPENSES,
  ORDER_MANAGEMENT_FINANCE_DASHBOARD,
  ORDER_MANAGEMENT_PAYOUT_BREAKDOWN,
  ORDER_MANAGEMENT_PRODUCT_REPORT,
  ORDER_MANAGEMENT_RETURN_REASON_ANALYSIS,
} from "../../routes/constant";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { userType } from "app/helpers/userType";

const OrderManagement = () => {
  const { isMA } = userType();
  const theme = useTheme();
  const navigate = useNavigate();
  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission")),
  );
  const [permission, setPermission] = useState({
    importPermission: false,
    orderPermission: false,
    returnPermission: false,
    statisticsPermission: false,
    paymentPermission: false,
    expensesPermission: false,
    dashboardPermission: false,
    productPermission: false,
    returnReasonPermission: false,
    payoutBreakdownPermission: false,
  });

  const hasMenuPermission = (permissionName) => {
    const permissionMap = {
      "import-meesho-files": permission.importPermission,
      "order-list": permission.orderPermission,
      "return-list": permission.returnPermission,
      "meesho-statistics": permission.statisticsPermission,
      "meesho-payment-list": permission.paymentPermission,
      "meesho-ads-list": permission.expensesPermission,
      "meesho-finance-dashboard": permission.dashboardPermission,
      "meesho-product-report": permission.productPermission,
      "meesho-return-reason": permission.returnReasonPermission,
      "meesho-payout-breakdown": permission.payoutBreakdownPermission,
    };

    return Boolean(permissionMap[permissionName]);
  };

  const getPermission = () => {
    if (isMA) {
      setPermission({
        importPermission: true,
        orderPermission: true,
        returnPermission: true,
        statisticsPermission: true,
        paymentPermission: true,
        expensesPermission: true,
        dashboardPermission: true,
        productPermission: true,
        returnReasonPermission: true,
        payoutBreakdownPermission: true,
      });
    } else {
      let permissionValue = {
        importPermission: false,
        orderPermission: false,
        returnPermission: false,
        statisticsPermission: false,
        paymentPermission: false,
        expensesPermission: false,
        dashboardPermission: false,
        productPermission: false,
        returnReasonPermission: false,
        payoutBreakdownPermission: false,
      };
      permissionData?.length &&
        permissionData.forEach(function (num) {
          if (num?.name === "import-meesho-files") {
            permissionValue.importPermission = true;
          }
          if (num?.name === "order-list") {
            permissionValue.orderPermission = true;
          }
          if (num?.name === "return-list") {
            permissionValue.returnPermission = true;
          }
          if (num?.name === "meesho-statistics") {
            permissionValue.statisticsPermission = true;
          }
          if (num?.name === "meesho-payment-list") {
            permissionValue.paymentPermission = true;
          }
          if (num?.name === "meesho-ads-list") {
            permissionValue.expensesPermission = true;
          }
          if (num?.name === "meesho-finance-dashboard") {
            permissionValue.dashboardPermission = true;
          }
          if (num?.name === "meesho-product-report") {
            permissionValue.productPermission = true;
          }
          if (num?.name === "meesho-return-reason") {
            permissionValue.returnReasonPermission = true;
          }
          if (num?.name === "meesho-payout-breakdown") {
            permissionValue.payoutBreakdownPermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  useEffect(() => {
    getPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const menuItems = [
    {
      title: "Import",
      description: "Import file with duplicate handling",
      icon: <UploadIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.primary.main,
      route: ORDER_MANAGEMENT_IMPORT,
      permission: "import-meesho-files",
    },
    {
      title: "Orders List",
      description: "View, search and filter orders with status management",
      icon: <ListIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.success.main,
      route: ORDER_MANAGEMENT_LIST,
      permission: "order-list",
    },
    {
      title: "Returns List",
      description: "View and manage all return records and claims",
      icon: <ReturnsListIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.error.main,
      route: ORDER_MANAGEMENT_RETURNS,
      permission: "return-list",
    },
    {
      title: "Statistics",
      description: "View order statistics and analytics dashboard",
      icon: <AnalyticsIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.info.main,
      route: ORDER_MANAGEMENT_STATISTICS,
      permission: "meesho-statistics",
    },
    {
      title: "Payment Reconciliation",
      description: "Process product Payment Reconciliation",
      icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.secondary.main,
      route: ORDER_MANAGEMENT_PAYMENT_PROCESS,
      permission: "meesho-payment-list",
    },
    {
      title: "Marketing And Expenses List",
      description: "Marketing And Expenses manages and tracks business costs.",
      icon: <RequestQuoteIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.warning.main,
      route: ORDER_MANAGEMENT_MARKETING_EXPENSES,
      permission: "meesho-ads-list",
    },
    {
      title: "Finance Dashboard",
      description: "Tracks revenue, expenses, and overall financial performance.",
      icon: <AutoGraphIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.success.main,
      route: ORDER_MANAGEMENT_FINANCE_DASHBOARD,
      permission: "meesho-finance-dashboard",
    },
    {
      title: "Payout Breakdown",
      description: "Month-wise Meesho payout, same as Seller Insights.",
      icon: <AccountBalanceIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.primary.dark,
      route: ORDER_MANAGEMENT_PAYOUT_BREAKDOWN,
      permission: "meesho-payout-breakdown",
    },
    {
      title: "Product Report",
      description: "Generates detailed reports on product performance and sales.",
      icon: <Inventory2Icon sx={{ fontSize: 40 }} />,
      color: theme.palette.error.main,
      route: ORDER_MANAGEMENT_PRODUCT_REPORT,
      permission: "meesho-product-report",
    },
    {
      title: "Return Reason Analysis",
      description: "Analyze return reasons by category, SKU, and trends. Import Meesho returns CSV.",
      icon: <ReturnReasonIcon sx={{ fontSize: 40 }} />,
      color: "#F43397",
      route: ORDER_MANAGEMENT_RETURN_REASON_ANALYSIS,
      permission: "meesho-return-reason",
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Order Management
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Manage your orders with import, tracking, and analytics
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {menuItems
          .filter((item) => hasMenuPermission(item.permission))
          .map((item, index) => (
          <Grid item xs={12} md={6} lg={4} key={index}>
            <Card
              sx={{
                height: "100%",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[8],
                },
              }}
              onClick={() => navigate(item.route)}
            >
              <CardContent
                sx={{
                  p: 3,
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    backgroundColor: alpha(item.color, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                    color: item.color,
                  }}
                >
                  {item.icon}
                </Box>
                <Typography variant="h5" component="h2" gutterBottom>
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {item.description}
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: item.color,
                    "&:hover": {
                      backgroundColor: item.color,
                      opacity: 0.9,
                    },
                  }}
                >
                  Access
                </Button>
              </CardContent>
            </Card>
          </Grid>
          ))}
      </Grid>
    </Box>
  );
};

export default OrderManagement;
